import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { ACCESS_QUERY_ROOT } from "@/api/access/access.queries";
import { EntitiesQueryKeys } from "@/api/entities/entities.queries";
import { PermissionsQueryKeys as SessionPermissionsQueryKeys } from "@/api/permissions/permissions.api";
import { PermissionsQueryKeys } from "@/api/roles-permissions/roles-permissions.queries";
import { refreshUserHistory, UserHistoryQueryKeys } from "@/api/user-management/userHistory";
import { UserManagementQueryKeys } from "@/api/user-management/userManagement.queries";
import { UserRecordQueryKeys } from "@/api/user-management/userRecord";
import { RoleHoldersQueryKeys } from "@/hooks/roles-permissions/useRoleHolders";
import { UserAdjustmentsQueryKeys } from "@/hooks/user-management/useUserAdjustments";
import { UserAccessQueryKeys } from "@/hooks/user-management/useUserRolesScope";
import { getActiveSessionState } from "@/hooks/useActiveSession";

/**
 * Every query that holds one person's record data, in the one list person writes refresh from
 * (SERV-3576). A write that touches a person adds to this list, never a second one. A function,
 * not a constant, because the hooks that own some of these keys import this module back.
 */
export const personRecordKeys = (userId: string) => ({
  /** The person read: the header, the Details tab and the Edit User page's entities. */
  record: [UserRecordQueryKeys.record, userId],
  /** Every role held, temporary roles included: Roles & Scope, the header and History's names. */
  heldRoles: [RoleHoldersQueryKeys.userRoles, userId],
  /**
   * Shared with the role Users tab's per-holder read; as a prefix it also covers the ServEngine
   * read that names the person's companies while acting in one (`usePersonCompanies`).
   */
  scope: [RoleHoldersQueryKeys.userScope, userId],
  /** Every login's read, ServEngine and each company: `[root, userId, login]`. */
  effectiveAccess: [UserAccessQueryKeys.effective, userId],
  adjustments: [UserAdjustmentsQueryKeys.list, userId],
  /** Keyed by adjustment, not person, so all of them. */
  adjustmentLogs: [UserAdjustmentsQueryKeys.log],
  /** History and Recent Changes: one read of every entry about the person (SERV-3581). */
  history: [UserHistoryQueryKeys.history, userId],
});

/** Lists that show the person among others: the users list rows and the Edit User page's read. */
const USERS_LIST_KEYS: QueryKey[] = [
  [UserManagementQueryKeys.roles],
  [UserManagementQueryKeys.userDetail],
];

/** Who holds each role: the roles list's counts, the role record and its Users tab. */
const ROLE_HOLDING_KEYS: QueryKey[] = [
  [PermissionsQueryKeys.getAllRoles],
  [PermissionsQueryKeys.getRoleById],
  [PermissionsQueryKeys.getRoleHolders],
];

/** Pickers that list people by name. */
const PEOPLE_PICKER_KEYS: QueryKey[] = [
  [UserManagementQueryKeys.userPickerOptions],
  [UserManagementQueryKeys.reportTo],
  [UserManagementQueryKeys.superAdminReportTo],
];

/**
 * The signed-in person's own access, menus and entities. The access read never goes stale on its
 * own (`staleTime: Infinity`), so an admin changing their own record keeps old menus without this.
 */
const OWN_SESSION_KEYS: QueryKey[] = [
  [ACCESS_QUERY_ROOT],
  [SessionPermissionsQueryKeys.root],
  [EntitiesQueryKeys.myEntities],
];

/**
 * What a write changed on the person:
 * - `roles`: a role or temporary role given or ended, an entity added, people added to or removed
 *   from a role;
 * - `scope`: a scope save (Edit Scope, Set Scope);
 * - `adjustment`: an adjustment added or changed;
 * - `profile`: anything the person is listed by in a people picker (Edit Details);
 * - `password`: a password set, which only adds a History row.
 */
export type PersonWrite = "roles" | "scope" | "adjustment" | "profile" | "password";

const changedKeys = (write: PersonWrite, userId: string): QueryKey[] => {
  const person = personRecordKeys(userId);
  switch (write) {
    case "roles":
      return [
        person.record,
        person.heldRoles,
        person.scope,
        person.effectiveAccess,
        person.adjustments,
        ...USERS_LIST_KEYS,
        ...ROLE_HOLDING_KEYS,
      ];
    case "scope":
      return [person.scope, person.effectiveAccess, [PermissionsQueryKeys.getRoleHolders]];
    case "adjustment":
      return [person.adjustments, person.adjustmentLogs, person.effectiveAccess];
    case "profile":
      return [person.record, ...USERS_LIST_KEYS, ...ROLE_HOLDING_KEYS, ...PEOPLE_PICKER_KEYS];
    case "password":
      return [];
  }
};

/**
 * Every write adds a History row: the person-level read lists what `/access` files under each
 * row's own id (temporary roles, adjustments, scope) beside what is filed under the person.
 */
const historyKeys = (_write: PersonWrite, userId: string): QueryKey[] => [
  personRecordKeys(userId).history,
];

/** Writes that change what the person can do or where. */
const CHANGES_ACCESS: Record<PersonWrite, boolean> = {
  roles: true,
  scope: true,
  adjustment: true,
  profile: false,
  password: false,
};

const isSignedInPerson = (userId: string) => {
  const signedIn = getActiveSessionState().userId;
  return signedIn != null && String(signedIn) === userId;
};

/** Each key once, so a write on many people refreshes a shared list once, not once per person. */
const distinct = (keys: QueryKey[]): QueryKey[] => [
  ...new Map(keys.map((key) => [JSON.stringify(key), key])).values(),
];

/** Refreshes every query that shows what the write changed on each of the people. */
export const refreshPersonRecord = (
  queryClient: QueryClient,
  people: string | string[],
  changed: PersonWrite | PersonWrite[],
) => {
  const userIds = typeof people === "string" ? [people] : people;
  const writes = typeof changed === "string" ? [changed] : changed;
  const each = (keysOf: (write: PersonWrite, userId: string) => QueryKey[]) =>
    distinct(writes.flatMap((write) => userIds.flatMap((userId) => keysOf(write, userId))));
  for (const queryKey of each(changedKeys)) {
    void queryClient.invalidateQueries({ queryKey });
  }
  for (const queryKey of each(historyKeys)) refreshUserHistory(queryClient, queryKey);
  if (writes.some((write) => CHANGES_ACCESS[write]) && userIds.some(isSignedInPerson)) {
    for (const queryKey of OWN_SESSION_KEYS) {
      void queryClient.invalidateQueries({ queryKey });
    }
  }
};
